<template>
  <div
    class="prpf"
    :class="{ 'prpf--disabled': disabled }"
    ref="wrapper"
  >
    <div v-if="showPicked" class="prpf__picked" :class="{ 'prpf__picked--locked': disabled }">
      <button
        v-if="!disabled"
        type="button"
        class="prpf__picked-label-btn"
        @click.stop="enterSearchMode"
      >
        {{ pickedLabel }}
      </button>
      <p v-else class="prpf__picked-label">{{ pickedLabel }}</p>
      <button
        v-if="clearable && !disabled"
        type="button"
        class="prpf__clear"
        aria-label="Clear selection"
        @click.stop="clearSelection"
      >
        ✕
      </button>
    </div>

    <div
      v-else
      class="prpf__search-block"
      :class="{
        'prpf__search-block--menu': showMenu,
        'prpf__search-block--empty': showNoResults,
      }"
    >
      <input
        ref="searchInputRef"
        type="text"
        class="prpf__input"
        :class="{
          'prpf__input--active': showMenu,
          'prpf__input--error': showNoResults,
        }"
        :value="query"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        @input="onInputEvent"
        @focus="openMenu"
        @click="openMenu"
        @keydown.esc="closeMenu"
      />

      <p v-if="showNoResults" class="prpf__error">
        {{ noResultsMessage }}
      </p>

      <div
        v-else-if="showMenu"
        class="prpf__dropdown"
        :class="{ 'prpf__dropdown--loading': dropdownLoading }"
        role="listbox"
      >
        <PayRangePickerResultsList
          :results="results"
          :loading="dropdownLoading"
          @select="selectOption"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import PayRangePickerResultsList from "./PayRangePickerResultsList.vue";
import {
  buildNoResultsMessage,
  buildPayRangePickerLabel,
} from "./payRangePickerFieldUtils.js";

const props = defineProps({
  modelValue: { type: String, default: null },
  results: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  placeholder: {
    type: String,
    default: "Search pay ranges by code or name",
  },
  clearable: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  openOnFocus: { type: Boolean, default: false },
  autofocus: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "search", "clear"]);

const query = ref("");
const isTyping = ref(false);
const wrapper = ref(null);
const searchInputRef = ref(null);
const debounceTimer = ref(null);
const autofocusTimer = ref(null);
const isOpen = ref(false);
const cachedSelection = ref(null);

const selectedOption = computed(() => {
  if (!props.modelValue) return null;
  return (
    props.results.find((option) => option.id === props.modelValue) ||
    (cachedSelection.value?.id === props.modelValue ? cachedSelection.value : null)
  );
});

const pickedLabel = computed(() => buildPayRangePickerLabel(selectedOption.value));

const showPicked = computed(() => Boolean(props.modelValue && !query.value));

const openedByFocus = computed(
  () => props.openOnFocus && isOpen.value && !props.disabled
);

const showMenu = computed(
  () => (Boolean(query.value) && !props.disabled) || openedByFocus.value
);

const dropdownLoading = computed(() => isTyping.value || props.loading);

const showNoResults = computed(
  () =>
    (Boolean(query.value) || openedByFocus.value) &&
    !dropdownLoading.value &&
    props.results.length === 0
);

const noResultsMessage = computed(() => buildNoResultsMessage(query.value));

function focusSearchInput() {
  searchInputRef.value?.focus();
}

function openMenu() {
  if (props.disabled) return;
  isOpen.value = true;
}

function enterSearchMode() {
  if (props.disabled) return;
  emit("update:modelValue", null);
  cachedSelection.value = null;
  query.value = "";
  openMenu();
  focusSearchInput();
}

function onInput(value) {
  if (props.disabled) return;

  if (props.modelValue) {
    emit("update:modelValue", null);
    cachedSelection.value = null;
  }

  query.value = value;
  isTyping.value = true;
  clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => {
    isTyping.value = false;
    emit("search", query.value);
  }, 300);
}

function onInputEvent(event) {
  onInput(event.target.value);
}

function selectOption(option) {
  if (props.disabled) return;
  query.value = "";
  isTyping.value = false;
  isOpen.value = false;
  cachedSelection.value = option;
  emit("update:modelValue", option.id);
}

function clearSelection() {
  if (props.disabled) return;
  emit("update:modelValue", null);
  emit("clear");
  cachedSelection.value = null;
  query.value = "";
  emit("search", "");
}

function clearQuery() {
  if (!query.value) return;
  query.value = "";
  isTyping.value = false;
  emit("search", "");
}

function closeMenu() {
  isOpen.value = false;
  clearQuery();
}

function handleOutsideClick(event) {
  if (wrapper.value && !wrapper.value.contains(event.target)) {
    closeMenu();
  }
}

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      query.value = "";
      isTyping.value = false;
      isOpen.value = false;
      const match = props.results.find((option) => option.id === value);
      if (match) cachedSelection.value = match;
    } else {
      cachedSelection.value = null;
    }
  }
);

watch(
  () => props.results,
  (results) => {
    if (!props.modelValue) return;
    const match = results.find((option) => option.id === props.modelValue);
    if (match) cachedSelection.value = match;
  }
);

onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
  if (props.autofocus) {
    // Deferred: the click that mounted the host is still bubbling to document and would read as an outside click.
    autofocusTimer.value = setTimeout(() => {
      focusSearchInput();
      openMenu();
    }, 0);
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleOutsideClick);
  clearTimeout(debounceTimer.value);
  clearTimeout(autofocusTimer.value);
});
</script>

<style scoped>
.prpf {
  width: 100%;
  font-family: Inter, sans-serif;
}

.prpf--disabled {
  pointer-events: none;
}

.prpf__search-block {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.prpf__search-block--menu {
  gap: 4px;
}

.prpf__search-block--empty {
  gap: 6px;
}

.prpf__input {
  appearance: none;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  color: #101828;
  font-family: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.prpf__input::placeholder {
  color: #667085;
}

.prpf__input--active {
  border-color: #101828;
  box-shadow: none;
}

.prpf__input--error {
  border-color: #f04438;
  box-shadow: none;
}

.prpf__input:disabled {
  background: #f9fafb;
  color: #667085;
}

.prpf__error {
  margin: 0;
  color: #f04438;
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
}

.prpf__dropdown {
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 4px 8px -2px rgba(16, 24, 40, 0.1);
  box-sizing: border-box;
}

.prpf__dropdown--loading {
  box-shadow: none;
}

.prpf__picked {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-sizing: border-box;
}

.prpf__picked--locked {
  background: #f9fafb;
}

.prpf__picked-label,
.prpf__picked-label-btn {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: #101828;
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  text-align: left;
}

.prpf__picked-label-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: text;
  font-family: inherit;
}

.prpf__clear {
  appearance: none;
  border: 0;
  background: transparent;
  flex-shrink: 0;
  cursor: pointer;
  color: #667085;
  font-size: 16px;
  line-height: 1;
  padding: 0;
}

.prpf__clear:hover {
  color: #344054;
}
</style>
